import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  configApiRef,
  errorApiRef,
  storageApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { getFlag } from '../../../helpers/helpers';
import { NeedsYouSourceId } from './types';

export type NeedsYouSourceInfo = {
  id: NeedsYouSourceId;
  title: string;
  /** Frontend feature flag of the plugin behind the source, if any. */
  flag?: string;
};

export const NEEDS_YOU_SOURCES: readonly NeedsYouSourceInfo[] = [
  { id: 'github-reviews', title: 'Reviews requested from you' },
  { id: 'github-my-prs', title: 'Your pull requests waiting on you' },
  {
    id: 'dependency-security',
    title: 'Security findings in your services',
    flag: 'dependency-security',
  },
  { id: 'blueprints', title: 'Failed Blueprint runs' },
  { id: 'locks', title: 'Locks you hold', flag: 'lock-manager' },
  { id: 'devboxes', title: 'Your devboxes', flag: 'devbox-manager' },
  {
    id: 'internal-apps',
    title: 'Your internal apps',
    flag: 'vibe-coded-apps',
  },
  {
    id: 'deployments',
    title: 'Deploys of your pull requests',
    flag: 'deployments',
  },
];

const BUCKET = 'home.needsYou';
const HIDDEN_KEY = 'hiddenSources';

/**
 * Sources this portal offers (their plugin is enabled) and which of them the
 * user turned off. The choice is kept with the user's other settings, so it
 * follows them across browsers when user settings are stored in the backend.
 */
export const useNeedsYouSettings = () => {
  const config = useApi(configApiRef);
  const errorApi = useApi(errorApiRef);
  const storageApi = useApi(storageApiRef);
  const storage = useMemo(() => storageApi.forBucket(BUCKET), [storageApi]);
  const available = NEEDS_YOU_SOURCES.filter(
    source => !source.flag || getFlag(config, source.flag),
  );

  const [hidden, setHiddenState] = useState<NeedsYouSourceId[]>(
    () => storage.snapshot<NeedsYouSourceId[]>(HIDDEN_KEY).value ?? [],
  );
  useEffect(() => {
    const subscription = storage
      .observe$<NeedsYouSourceId[]>(HIDDEN_KEY)
      .subscribe(snapshot => setHiddenState(snapshot.value ?? []));
    return () => subscription.unsubscribe();
  }, [storage]);

  // Applied at once; if saving fails the menu goes back to the saved
  // choice and says why, rather than silently reverting on the next load.
  const setHidden = useCallback(
    (next: NeedsYouSourceId[]) => {
      setHiddenState(next);
      storage.set(HIDDEN_KEY, next).catch(error => {
        setHiddenState(
          storage.snapshot<NeedsYouSourceId[]>(HIDDEN_KEY).value ?? [],
        );
        errorApi.post(
          new Error(`Couldn't save your Needs you sources: ${error}`),
        );
      });
    },
    [storage, errorApi],
  );

  const isOn = (id: NeedsYouSourceId) =>
    available.some(source => source.id === id) && !hidden.includes(id);

  return { available, hidden, setHidden, isOn };
};
